﻿import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import {
  changeMyPassword,
  getSettingsSnapshot,
  listSecurityAuditLogs,
  updateSecuritySettings,
  type SecurityAuditLogItem,
} from "@/lib/api";

type SecurityForm = {
  sessionTimeoutMinutes: string;
  mfaForAdmins: boolean;
  passwordMinLength: string;
  passwordRotationDays: string;
  lockoutAttempts: string;
  lockoutMinutes: string;
};

const defaultForm: SecurityForm = {
  sessionTimeoutMinutes: "60",
  mfaForAdmins: true,
  passwordMinLength: "10",
  passwordRotationDays: "90",
  lockoutAttempts: "5",
  lockoutMinutes: "15",
};

export default function SeguridadTab() {
  const { session } = useSession();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState<SecurityForm>(defaultForm);
  const [auditLogs, setAuditLogs] = useState<SecurityAuditLogItem[]>([]);

  const [showChangePass, setShowChangePass] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [passForm, setPassForm] = useState({ actual: "", nueva: "", confirmar: "" });
  const [passError, setPassError] = useState("");
  const [passOk, setPassOk] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const [snapshot, logs] = await Promise.all([
          getSettingsSnapshot(session),
          listSecurityAuditLogs(session, 50),
        ]);

        if (cancelled) return;

        const security = snapshot.security;
        setForm({
          sessionTimeoutMinutes: String(security?.sessionTimeoutMinutes ?? 60),
          mfaForAdmins: Boolean(security?.mfaForAdmins ?? true),
          passwordMinLength: String(security?.passwordMinLength ?? 10),
          passwordRotationDays: String(security?.passwordRotationDays ?? 90),
          lockoutAttempts: String(security?.lockoutAttempts ?? 5),
          lockoutMinutes: String(security?.lockoutMinutes ?? 15),
        });
        setAuditLogs(logs);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudo cargar seguridad");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const visibleLogs = useMemo(() => auditLogs.slice(0, 20), [auditLogs]);

  function setField<K extends keyof SecurityForm>(key: K, value: SecurityForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function handleSave(): Promise<void> {
    if (!session) return;

    const sessionTimeoutMinutes = Number(form.sessionTimeoutMinutes);
    const passwordMinLength = Number(form.passwordMinLength);
    const passwordRotationDays = Number(form.passwordRotationDays);
    const lockoutAttempts = Number(form.lockoutAttempts);
    const lockoutMinutes = Number(form.lockoutMinutes);

    if (!Number.isFinite(sessionTimeoutMinutes) || sessionTimeoutMinutes < 0) {
      setError("Tiempo de sesión inválido");
      return;
    }
    if (!Number.isFinite(passwordMinLength) || passwordMinLength < 8) {
      setError("La longitud mínima de contraseña debe ser al menos 8");
      return;
    }
    if (!Number.isFinite(passwordRotationDays) || passwordRotationDays < 0) {
      setError("Rotación de contraseña inválida");
      return;
    }
    if (!Number.isFinite(lockoutAttempts) || lockoutAttempts < 1) {
      setError("Intentos máximos inválidos");
      return;
    }
    if (!Number.isFinite(lockoutMinutes) || lockoutMinutes < 1) {
      setError("Minutos de bloqueo inválidos");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await updateSecuritySettings(session, {
        sessionTimeoutMinutes: Math.trunc(sessionTimeoutMinutes),
        mfaForAdmins: form.mfaForAdmins,
        passwordMinLength: Math.trunc(passwordMinLength),
        passwordRotationDays: Math.trunc(passwordRotationDays),
        lockoutAttempts: Math.trunc(lockoutAttempts),
        lockoutMinutes: Math.trunc(lockoutMinutes),
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);

      const logs = await listSecurityAuditLogs(session, 50);
      setAuditLogs(logs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar seguridad");
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePass(): Promise<void> {
    if (!session) return;

    if (!passForm.actual || !passForm.nueva || !passForm.confirmar) {
      setPassError("Completa todos los campos.");
      return;
    }
    if (passForm.nueva !== passForm.confirmar) {
      setPassError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (passForm.nueva.length < 8) {
      setPassError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setChangingPass(true);
    setPassError("");
    try {
      await changeMyPassword(session, {
        currentPassword: passForm.actual,
        newPassword: passForm.nueva,
      });
      setPassOk(true);
      setPassForm({ actual: "", nueva: "", confirmar: "" });
      setTimeout(() => {
        setPassOk(false);
        setShowChangePass(false);
      }, 2500);
    } catch (e) {
      setPassError(e instanceof Error ? e.message : "No se pudo cambiar contraseña");
    } finally {
      setChangingPass(false);
    }
  }

  return (
    <div className="space-y-5">
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-gray-500">Cargando seguridad...</div> : null}

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50">
            <i className="ri-lock-line text-blue-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Seguridad de Acceso</h2>
            <p className="text-xs text-gray-400">Parámetros reales de sesión y políticas de seguridad</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Tiempo de sesión (minutos)">
            <input
              type="number"
              value={form.sessionTimeoutMinutes}
              onChange={(e) => setField("sessionTimeoutMinutes", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="MFA para administradores">
            <button
              type="button"
              onClick={() => setField("mfaForAdmins", !form.mfaForAdmins)}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${form.mfaForAdmins ? "bg-emerald-500" : "bg-gray-200"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${form.mfaForAdmins ? "left-5" : "left-0.5"}`} />
            </button>
          </Field>

          <Field label="Largo mínimo de contraseña">
            <input
              type="number"
              value={form.passwordMinLength}
              onChange={(e) => setField("passwordMinLength", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="Rotación de contraseña (días)">
            <input
              type="number"
              value={form.passwordRotationDays}
              onChange={(e) => setField("passwordRotationDays", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="Intentos fallidos antes de bloqueo">
            <input
              type="number"
              value={form.lockoutAttempts}
              onChange={(e) => setField("lockoutAttempts", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="Duración de bloqueo (minutos)">
            <input
              type="number"
              value={form.lockoutMinutes}
              onChange={(e) => setField("lockoutMinutes", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
              <i className="ri-key-2-line text-amber-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Contraseña</h2>
              <p className="text-xs text-gray-400">Cambio de contraseña real del usuario autenticado</p>
            </div>
          </div>
          <button
            onClick={() => setShowChangePass(!showChangePass)}
            className="px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
          >
            {showChangePass ? "Cancelar" : "Cambiar contraseña"}
          </button>
        </div>

        {showChangePass ? (
          <div className="space-y-3 pt-2">
            <PasswordField
              label="Contraseña actual"
              value={passForm.actual}
              onChange={(value) => setPassForm((prev) => ({ ...prev, actual: value }))}
            />
            <PasswordField
              label="Nueva contraseña"
              value={passForm.nueva}
              onChange={(value) => setPassForm((prev) => ({ ...prev, nueva: value }))}
            />
            <PasswordField
              label="Confirmar nueva contraseña"
              value={passForm.confirmar}
              onChange={(value) => setPassForm((prev) => ({ ...prev, confirmar: value }))}
            />

            {passError ? <div className="text-xs text-red-600">{passError}</div> : null}
            {passOk ? <div className="text-xs text-emerald-600">Contraseña actualizada correctamente.</div> : null}

            <button
              onClick={() => void handleChangePass()}
              disabled={changingPass}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {changingPass ? "Actualizando..." : "Actualizar contraseña"}
            </button>
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100">
            <i className="ri-file-list-2-line text-gray-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Registro de Auditoría</h2>
            <p className="text-xs text-gray-400">Últimas acciones reales registradas en base de datos</p>
          </div>
        </div>

        <div className="space-y-2">
          {visibleLogs.length === 0 ? (
            <div className="text-sm text-gray-500">Sin registros de auditoría.</div>
          ) : (
            visibleLogs.map((log) => (
              <div key={log.id} className="flex items-center gap-4 px-3 py-2.5 rounded-lg bg-gray-50 text-sm">
                <div className="w-4 h-4 flex items-center justify-center text-gray-400 flex-shrink-0">
                  <i className="ri-history-line text-xs" />
                </div>
                <span className="flex-1 text-gray-700">
                  [{toModuleLabel(log.module)}] {toActionLabel(log.action)} · {log.entityName}
                </span>
                <span className="text-gray-500 text-xs hidden md:block">{log.userName}</span>
                <span className="text-gray-400 text-xs whitespace-nowrap">{toRelativeTime(log.createdAt)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line" /></div>
            Configuración guardada
          </div>
        ) : null}
        <button
          onClick={() => void handleSave()}
          disabled={saving || loading}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PasswordField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
      />
    </div>
  );
}

function toRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(1, Math.round(diffMs / 60000));
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay === 1) return "Ayer";
  return `Hace ${diffDay} días`;
}

function toActionLabel(action: string): string {
  const value = action.trim();
  if (!value) return "Acción";
  return value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function toModuleLabel(module: string): string {
  const labels: Record<string, string> = {
    auth: "Autenticación",
    settings: "Configuración",
    customers: "Clientes",
    contracts: "Contratos",
    operations: "Operaciones",
    inventory: "Bodega",
    payments: "Pagos",
    billing: "Facturación",
    readings: "Lecturas",
  };
  return labels[module] ?? module;
}
